import { CarrierLogo } from "@/components/carriers/CarrierLogo";
import { WorkflowRail } from "@/components/tms/WorkflowRail";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { apiClient } from "@/lib/api/client";
import type { BookResponse } from "@/lib/api/types";
import { formatCurrency } from "@/lib/tms/format";
import { findRecentQuote, readRecentQuotes, type QuoteMode } from "@/lib/tms/recent";
import { ArrowRight } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

const QUOTE_SELECTION_KEY = "shippeek-selected-quote";
const NO_REFERENCE = "__none__";

type Mode = QuoteMode;

function findValueByKey(value: unknown, keys: Set<string>, depth = 0): string | null {
  if (depth > 6 || value === null || value === undefined) return null;
  if (typeof value === "string") return null;
  if (typeof value !== "object") return null;

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findValueByKey(item, keys, depth + 1);
      if (found) return found;
    }
    return null;
  }

  for (const [rawKey, rawValue] of Object.entries(value)) {
    const normalized = rawKey.trim().toLowerCase();
    if (keys.has(normalized)) {
      if (typeof rawValue === "string" && rawValue.trim()) return rawValue.trim();
      if (typeof rawValue === "number" && Number.isFinite(rawValue)) return String(rawValue);
    }
    const nested = findValueByKey(rawValue, keys, depth + 1);
    if (nested) return nested;
  }

  return null;
}

function extractTrackingInfo(result: BookResponse | null): { trackingNumber?: string; carrierCode?: string } {
  if (!result) return {};
  const trackingNumber = findValueByKey(result, new Set(["trackingnumber", "tracking_number", "pronumber", "pro_number"]));
  const carrierCode = findValueByKey(result, new Set(["carriercode", "carrier_code", "carrier"]));
  return {
    trackingNumber: trackingNumber ?? undefined,
    carrierCode: carrierCode ?? undefined,
  };
}

export default function ShipmentBookPage() {
  const navigate = useNavigate();

  const [mode, setMode] = React.useState<Mode>("ltl");
  const [refId, setRefId] = React.useState("");
  const [rateId, setRateId] = React.useState("");
  const [shipperContactName, setShipperContactName] = React.useState("");
  const [shipperEmail, setShipperEmail] = React.useState("");
  const [consigneeContactName, setConsigneeContactName] = React.useState("");
  const [consigneeEmail, setConsigneeEmail] = React.useState("");
  const [specialInstructions, setSpecialInstructions] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState<BookResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [recentReferences, setRecentReferences] = React.useState(() => readRecentQuotes());

  const refreshRecentReferences = React.useCallback(() => {
    setRecentReferences(readRecentQuotes());
  }, []);

  React.useEffect(() => {
    const raw = localStorage.getItem(QUOTE_SELECTION_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as { mode?: Mode; refId?: string; rateId?: string };
      if (parsed.mode) setMode(parsed.mode);
      if (parsed.refId) setRefId(parsed.refId);
      if (parsed.rateId) setRateId(parsed.rateId);
    } catch {
      // no-op
    } finally {
      refreshRecentReferences();
    }
  }, [refreshRecentReferences]);

  const selectedRecentReference = React.useMemo(
    () => (refId ? findRecentQuote(refId) : null),
    [refId],
  );

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        id: refId,
        rateId,
        shipperContactName,
        shipperEmail,
        consigneeContactName,
        consigneeEmail,
        specialInstructions: specialInstructions || undefined,
      };

      const response = mode === "parcel" ? await apiClient.bookParcel(payload) : await apiClient.bookLtl(payload);
      setResult(response);
      refreshRecentReferences();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Booking failed.");
    } finally {
      setLoading(false);
    }
  };

  const trackingInfo = React.useMemo(() => extractTrackingInfo(result), [result]);

  return (
    <div className="space-y-6">
      <WorkflowRail current="book" />

      <Card>
        <CardHeader className="gap-3 md:flex md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle>Book Shipment</CardTitle>
            <CardDescription>Use a retrieved/quoted reference and select rate to create shipment booking.</CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => navigate("/quotes/retrieve")}>
              Retrieve Quote
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => navigate("/shipments")}>
              All Shipments
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={onSubmit}>
            {/* Quote reference section */}
            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Quote Reference</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Mode</Label>
                  <Select value={mode} onValueChange={(value: Mode) => setMode(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ltl">LTL</SelectItem>
                      <SelectItem value="parcel">Parcel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Reference</Label>
                  <Select
                    value={refId || NO_REFERENCE}
                    onValueChange={(value) => {
                      if (value === NO_REFERENCE) {
                        setRefId("");
                        return;
                      }
                      setRefId(value);
                      const snapshot = findRecentQuote(value);
                      if (snapshot?.mode) setMode(snapshot.mode);
                      if (snapshot?.rates?.[0]) setRateId(snapshot.rates[0].ref);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select reference" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_REFERENCE}>Manual input</SelectItem>
                      {recentReferences.map((entry) => (
                        <SelectItem key={entry.refId} value={entry.refId}>
                          {entry.refId} · {entry.mode.toUpperCase()} · {entry.rates.length} rates
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input value={refId} onChange={(event) => setRefId(event.target.value)} placeholder="Ref ID" required />
                </div>
                <div className="space-y-1.5">
                  <Label>Rate ID</Label>
                  {selectedRecentReference?.rates.length ? (
                    <Select value={rateId} onValueChange={setRateId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select rate" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedRecentReference.rates.map((rate) => (
                          <SelectItem key={rate.ref} value={rate.ref}>
                            <span className="flex items-center gap-2">
                              <CarrierLogo code={rate.carrierCode} name={rate.carrier} className="size-6 rounded" />
                              <span>{rate.carrier} · {rate.serviceDescription || rate.serviceType} · {formatCurrency(rate.total)}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : null}
                  <Input value={rateId} onChange={(event) => setRateId(event.target.value)} placeholder="Rate ID" required />
                </div>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Shipper details section */}
            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Shipper Details</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Contact Name</Label>
                  <Input value={shipperContactName} onChange={(event) => setShipperContactName(event.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input value={shipperEmail} onChange={(event) => setShipperEmail(event.target.value)} required />
                </div>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Consignee details section */}
            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Consignee Details</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Contact Name</Label>
                  <Input value={consigneeContactName} onChange={(event) => setConsigneeContactName(event.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input value={consigneeEmail} onChange={(event) => setConsigneeEmail(event.target.value)} required />
                </div>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Additional info section */}
            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Additional Info</p>
              <div className="space-y-1.5">
                <Label>Special Instructions</Label>
                <Textarea value={specialInstructions} onChange={(event) => setSpecialInstructions(event.target.value)} />
              </div>
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}

            <Button type="submit" disabled={loading}>
              {loading ? "Booking..." : "Book Shipment"}
            </Button>
          </form>

          {result ? (
            <div className="mt-4 space-y-3 rounded-lg border-[0.5px] border-border bg-muted/30 p-3">
              <p className="text-sm font-medium">Booking created</p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => navigate("/shipments")}>
                  Open Shipments
                </Button>
                {trackingInfo.trackingNumber ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      navigate(
                        `/tracking?tracking=${encodeURIComponent(trackingInfo.trackingNumber ?? "")}&carrier=${encodeURIComponent(
                          trackingInfo.carrierCode ?? "",
                        )}`,
                      )
                    }
                  >
                    Track Shipment
                    <ArrowRight className="size-4" />
                  </Button>
                ) : null}
              </div>
              <pre className="overflow-auto text-xs">{JSON.stringify(result, null, 2)}</pre>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
